<script lang="ts">
  import type { PageCollectionTheme } from '$lib/sanity/schemas';
  import { getContext } from 'svelte';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { ResponsiveVars } from '@twist/core';
  import { cx } from '@emotion/css';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    content: string;
    sourceMap?: ContentSourceMap | undefined;
    path?: StudioPathLike;
    // Speed of animation in pixels per second
    speed?: number;
  }

  let { content, sourceMap = undefined, path = [], speed = 20 }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(content, sourceMap, sanityStudioURL));

  let runningLaneWidth: number = $state(0);
  let contentWidth: number = $state(0);

  // Calculate number of repeated content needed to fill screen.
  // Doubled to have enough to scroll content left, plus total number
  // needs to be even to avoid visual break in translate -50% animation
  let numRepeatedContent = $derived(
    runningLaneWidth && contentWidth ? Math.ceil(runningLaneWidth / contentWidth) * 2 : 0
  );

  // Calculate animation time with speed = distance / time.
  // Distance is the the total width of content divided by 2 because
  // we are only translating to -50% to have a seamless scroll.
  let animationTime = $derived((contentWidth * numRepeatedContent) / 2 / speed);

  const theme = getContext<PageCollectionTheme>('theme');
  const themeMobile = theme?.text?.mobile?.runningLane;
  const themeDesktop = theme?.text?.desktop?.runningLane;

  const fontSizeVarInput = toFluidVarInput(
    parseInt(themeMobile?.fontSize ?? '') || 8,
    parseInt(themeDesktop?.fontSize ?? '') || 12
  );
  const letterSpacingVarInput = toFluidVarInput(
    parseInt(themeMobile?.letterSpacing ?? '6'),
    parseInt(themeDesktop?.letterSpacing ?? '4')
  );

  let responsiveVars = $derived(
    new ResponsiveVars()
      .addFluid('font-size', fontSizeVarInput)
      .addFluid('letter-spacing', letterSpacingVarInput)
      .addResponsive('theme-color', {
        min: themeMobile?.color,
        lg: themeDesktop?.color
      })
      .addResponsive('theme-line-height', {
        min: themeMobile?.lineHeight ?? '130%',
        lg: themeDesktop?.lineHeight ?? '160%'
      })
      .addResponsive('theme-font-family', {
        min: themeMobile?.fontFamily,
        lg: themeDesktop?.fontFamily
      })
      .addResponsive('theme-font-weight', {
        min: themeMobile?.fontWeight,
        lg: themeDesktop?.fontWeight
      })
      .addResponsive('theme-text-transform', {
        min: themeMobile?.textTransform,
        lg: themeDesktop?.textTransform
      })
      .toCSS()
  );
</script>

<div
  class={cx(responsiveVars, 'running-lane')}
  style="--animation-time: {animationTime}s"
  bind:clientWidth={runningLaneWidth}
  data-sanity={encodeDataAttribute(path)}
>
  <div class="content">
    <span bind:clientWidth={contentWidth}>
      {content}
    </span>
    {#if numRepeatedContent}
      {#each { length: numRepeatedContent - 1 } as _}
        <span aria-hidden="true">
          {content}
        </span>
      {/each}
    {/if}
  </div>
</div>

<style lang="scss">
  @import 'src/scss/accessibility';
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .running-lane {
    display: block;
    overflow: hidden;
    position: relative;
    border-bottom: 1px solid
      var(--theme-color, var(--theme-general-foreground-color, $general-foreground-color));
  }

  .content {
    animation: marquee var(--animation-time) linear infinite;
    display: flex;
    padding-bottom: var(--twist-core-spacing-3);
    position: relative;
    white-space: nowrap;
    width: fit-content;

    &:hover {
      animation-play-state: paused;
    }
  }

  span {
    padding-right: var(--twist-core-spacing-6);

    font-size: var(--font-size);
    letter-spacing: var(--letter-spacing);
    color: var(--theme-color, var(--theme-general-foreground-color, $general-foreground-color));
    font-family: var(--theme-font-family, var(--font-family-primary));
    font-weight: var(--theme-font-weight, 600);
    line-height: var(--theme-line-height, 130%);
    text-transform: var(--theme-text-transform, uppercase);
  }

  @keyframes marquee {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(-50%, 0, 0);
    }
  }

  @include prefers-reduce-motion {
    .content {
      animation: none;
    }
  }
</style>
